import { z } from 'zod';
import { OffsetPaginationParamsSchema } from '../offset-pagination.js';
// ---------------------------------------------------------------------------
// Trial laboratories — a laboratory used within ONE trial (domain 5,
// REQ-KIT-001/002; legacy `Laboratory`, dbase [s.23]).
//
// The global reference base `Laboratories` (immutable address/phones) and the
// lab contacts `Lab_contact` are UNDEFINED in the legacy source (K-5,
// Q-KIT-001/Q-KIT-002). On 2026-09-27 the user chose to start from the
// trial-scoped entity only and keep the global base as an open question instead
// of inventing its shape — so the re-supply data (REQ-KIT-002: ordering
// description, e-mail, WWW + the local-override flag) lives on this entity.
//
// `trialId` is deliberately NOT updatable: a laboratory cannot move between
// trials after creation (its kits, guidelines and ordering rules belong to one
// protocol).
// ---------------------------------------------------------------------------
export const TrialLaboratoryDtoSchema = z.object({
    id: z.number().int().positive(),
    tenantId: z.number().int().positive(),
    trialId: z.number().int().positive(),
    name: z.string().min(1).max(255),
    notes: z.string().nullable(),
    resupplyLocal: z.boolean(),
    resupplyDescription: z.string().nullable(),
    resupplyEmail: z.email('Niepoprawny format adresu e-mail').max(320).nullable(),
    resupplyWww: z.url('Niepoprawny format adresu strony WWW').max(512).nullable(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
});
export const CreateTrialLaboratoryRequestSchema = z.object({
    trialId: z.number().int().positive(),
    name: z.string().min(1, 'Nazwa laboratorium jest wymagana').max(255),
    notes: z.string().max(10_000).optional(),
    // REQ-KIT-002: when the trial's ordering rules differ from the laboratory's
    // main mode, they are stored separately (legacy `Re-supply_local`).
    resupplyLocal: z.boolean().default(true),
    resupplyDescription: z.string().max(10_000).optional(),
    resupplyEmail: z.email('Niepoprawny format adresu e-mail').max(320).optional(),
    resupplyWww: z.url('Niepoprawny format adresu strony WWW').max(512).optional(),
});
/**
 * `strict()` on purpose: a laboratory NEVER moves between trials, so a payload
 * carrying `trialId` (or any other unknown field) is a client error that must be
 * answered with 400 instead of being silently stripped — a silently ignored
 * `trialId` would look like a successful move in the UI.
 */
export const UpdateTrialLaboratoryRequestSchema = z
    .object({
    name: z.string().min(1, 'Nazwa laboratorium jest wymagana').max(255).optional(),
    notes: z.string().max(10_000).nullable().optional(),
    resupplyLocal: z.boolean().optional(),
    resupplyDescription: z.string().max(10_000).nullable().optional(),
    resupplyEmail: z.email('Niepoprawny format adresu e-mail').max(320).nullable().optional(),
    resupplyWww: z.url('Niepoprawny format adresu strony WWW').max(512).nullable().optional(),
})
    .strict();
export const TrialLaboratoryListQuerySchema = OffsetPaginationParamsSchema.extend({
    /** Narrow the catalogue to one trial (optional — the site-wide list is valid). */
    trialId: z.coerce.number().int().positive().optional(),
    search: z.string().max(100).optional(),
});
//# sourceMappingURL=laboratories.schema.js.map